import * as React from "react";
import ScoringCalendar from "./scoringCalendar";
import ScoringSnapshot from "./scoringSnapshot";
import ScoringReportDownloads from "./scoringReportDownloads";
import { Stack, Typography } from "@mui/material";
import DetailPage, { DetailPageTabListType } from "../../components/common/detailPage/detailPage";
import { SimpleBreadcrumbs } from "../../components/navigation/simpleBreadcrumbs";
import ProductSalesCard from "./productSales/productSalesCard";
import { useParams } from "react-router-dom";
import { FeatureFlag } from "../../featureFlags";
import {
    eGroupIds,
    eRoleIds,
    UserClaimsService,
} from "../../services/currentUserService/currentUserService";
import GroupReports from "./groupReports";
import AdministratorGroupsCard from "./administratorGroups/administratorGroupsCard";
import AdministratorsCard from "./administrators/administratorsCard";
import FacilitatorsCard from "./facilitators/facilitatorsCard";

export type AssessmentsProps = {
    tabIndex: string;
};

export const Assessments: React.FC = (props) => {
    const [actualTab, setActualTab] = React.useState<number>(0);
    const [filterGroupId, setFilterGroupId] = React.useState<number | undefined>(undefined);

    const features = React.useContext(FeatureFlag);
    const claimsService = new UserClaimsService();

    const params = useParams<AssessmentsProps>();

    const isSuperAdmin = claimsService.IsUserSuperAdmin();
    const isClientAdmin = claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);
    const isCclAdminProductSaleManager =
        claimsService.IsUserInGroups([eGroupIds.eCCLAdmin]) &&
        claimsService.IsUserInAppRoles([eRoleIds.eProductSaleManager]);
    const showFacilitatorsTab = isSuperAdmin || isCclAdminProductSaleManager;

    const showGroupReportsTab =
        features?.groupReports?.active &&
        (isSuperAdmin ||
            (claimsService.IsUserInAppRoles([eRoleIds.eGroupReportManager]) &&
                claimsService.IsUserCclAdminOrSuperAdmin()));

    React.useEffect(() => {
        if (params.tabIndex) {
            setActualTab(+params.tabIndex);
        }
    }, [params.tabIndex]);

    React.useEffect(() => {
        if (isClientAdmin && !showFacilitatorsTab) {
            setActualTab(7);
        }
    }, [isClientAdmin, showFacilitatorsTab]);

    const changeToAdminsTab = (groupId: number) => {
        setFilterGroupId(groupId);
        setActualTab(6);
    };

    const handleTabChangeRequest = (event: React.SyntheticEvent, newValue: number) => {
        setActualTab(newValue);
    };

    const tabList: DetailPageTabListType = [
        ["Scoring Calendar", 0, <ScoringCalendar />, !isClientAdmin],
        ["Scoring Status", 1, <ScoringSnapshot />, !isClientAdmin],
        ["Product Sales", 3, <ProductSalesCard />, !isClientAdmin],
        ["Assessment Reports", 2, <ScoringReportDownloads />, !isClientAdmin],
        ["Group Reports", 4, <GroupReports />, showGroupReportsTab && !isClientAdmin],
        [
            "Administrator Groups",
            5,
            <AdministratorGroupsCard onClickGroup={changeToAdminsTab} />,
            isSuperAdmin,
        ],
        [
            "Administrators",
            6,
            <AdministratorsCard filteredGroupId={filterGroupId} />,
            isSuperAdmin,
        ],
        [
            "Facilitators",
            8,
            <FacilitatorsCard />,
            showFacilitatorsTab,
        ],
    ];

    const breadcrumbs = (
        <Stack direction={"row"} spacing={3}>
            <SimpleBreadcrumbs>
                <Typography variant="h6" color="text.primary">
                    Assessments
                </Typography>
            </SimpleBreadcrumbs>
        </Stack>
    );

    return (
        <div>
            <DetailPage
                descriptor={"session"}
                breadcrumbs={breadcrumbs}
                tablist={tabList}
                handleTabChange={handleTabChangeRequest}
                activeTab={actualTab}
            />
        </div>
    );
};

export default Assessments;
